/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import React, { useEffect, useRef, useState } from "react";

interface LazyVideoProps {
    src: string;
    type?: string;
    className?: string;
    threshold?: number;
    loop?: boolean;
    muted?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
}

const LazyVideo: React.FC<LazyVideoProps> = ({
                                                 src,
                                                 type = "video/mp4",
                                                 className = "",
                                                 threshold = 0.25,
                                                 loop = true,
                                                 muted = true,
                                                 autoPlay = true,
                                                 controls = false,
                                             }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [shouldLoad, setShouldLoad] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShouldLoad(true);
                        if (autoPlay && videoRef.current) {
                            videoRef.current.play().catch(() => {
                                /* ignore autoplay errors */
                            });
                        }
                        observer.disconnect();
                    }
                });
            },
            { threshold }
        );

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        return () => {
            if (videoRef.current) {
                observer.unobserve(videoRef.current);
            }
        };
    }, [autoPlay, threshold]);

    return (
        <video
            ref={videoRef}
            className={className}
            autoPlay={autoPlay}
            muted={muted}
            loop={loop}
            controls={controls}
            playsInline
            preload="none"
        >
            {shouldLoad && <source src={src} type={type} />}
            Your browser does not support the video tag.
        </video>
    );
};

export default LazyVideo;